import type { PlanDraft } from './typology-draft';

/**
 * O arquivo da prancha virando a imagem sobre a qual se traça.
 *
 * Saiu de `PlanTracer.tsx` na Fase 24, quando a prancha passou a ser do servidor: renderiza
 * quem escolhe o arquivo na tela **e** quem abre a planta noutra máquina, a partir do que
 * o servidor devolveu. As duas pontas precisam da mesma renderização, ou a mesma folha
 * teria duas larguras e a escala de uma não serviria à outra (RN-247).
 *
 * **A prancha abre na qualidade do arquivo** (16/10/2026). Antes, toda prancha passava por um
 * `canvas` de 3000 px de largura e saía JPEG a 82%: um PNG de 8000 px chegava à tela com 3000,
 * e as linhas de 1 px — que são o desenho — viravam cinza borrado; ampliar não devolvia o que
 * já tinha sido jogado fora. Agora:
 *
 * - **a imagem** abre com os próprios bytes, no tamanho dela: nada se reduz nem se recodifica,
 *   a não ser acima do que o aparelho desenha (`areaMaximaDoAparelho`) — e aí em PNG, sem perda;
 * - **o PDF**, que é vetor, é rasterizado com toda a área que o aparelho aguenta, em PNG.
 *
 * Mudar a largura não descalibra nada: a escala é guardada com a largura em que foi medida
 * (RN-247), e quem abre converte.
 */

/** A página, de qualquer origem: o arquivo escolhido ou a URL que o servidor devolveu. */
export type Renderizador = (fonte: Blob | string) => Promise<PlanDraft>;

/**
 * A maior área, em pixels, que o aparelho desenha num `canvas`.
 *
 * O Safari do iPad recusa canvas acima de 16,7 MP (4096²) — e recusa em silêncio, com a página
 * em branco. Por isso o aparelho de toque fica nesse teto. No computador, 50 MP dão um A1 a
 * cerca de 8.300 px de largura: o canto de um quarto com poucos milímetros por pixel.
 */
export const AREA_NO_TABLET = 16_777_216;
export const AREA_NO_COMPUTADOR = 50_000_000;

export function areaMaximaDoAparelho(
  aparelho: { readonly maxTouchPoints?: number } | undefined = globalThis.navigator,
): number {
  return (aparelho?.maxTouchPoints ?? 0) > 1 ? AREA_NO_TABLET : AREA_NO_COMPUTADOR;
}

/**
 * A escala de rasterização de uma página de PDF (em pontos): a maior que cabe na área do
 * aparelho, sem passar de 6× — acima disso o arquivo de traço já não ganha nada que se veja.
 */
export function escalaDoPdf(largura: number, altura: number, areaMaxima: number): number {
  return Math.min(6, Math.sqrt(areaMaxima / (largura * altura)));
}

/**
 * O tamanho em que uma imagem abre: o dela, a não ser que passe da área do aparelho — e aí
 * reduzida na mesma proporção, só o bastante para caber.
 */
export function tamanhoDaImagem(
  largura: number,
  altura: number,
  areaMaxima: number,
): { readonly largura: number; readonly altura: number; readonly reduzida: boolean } {
  const area = largura * altura;
  if (area <= areaMaxima) return { largura, altura, reduzida: false };
  const fator = Math.sqrt(areaMaxima / area);
  return {
    largura: Math.floor(largura * fator),
    altura: Math.floor(altura * fator),
    reduzida: true,
  };
}

/**
 * Renderiza a prancha, seja PDF ou imagem.
 *
 * O tipo decide o caminho, e o do `Blob` que veio do servidor é o que o servidor leu dos
 * primeiros bytes — não a extensão que alguém escreveu.
 */
export const renderizarPrancha: Renderizador = async (fonte) => {
  const arquivo = typeof fonte === 'string' ? await (await fetch(fonte)).blob() : fonte;
  return arquivo.type.includes('pdf') ? renderizarPdf(arquivo) : carregarImagem(arquivo);
};

async function renderizarPdf(arquivo: Blob): Promise<PlanDraft> {
  const pdfjs = await import('pdfjs-dist');
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

  const documento = await pdfjs.getDocument({ data: await arquivo.arrayBuffer() }).promise;
  const pagina = await documento.getPage(1);

  const original = pagina.getViewport({ scale: 1 });
  // O PDF é vetor: rasterizar acima de 1:1 é ganho real de nitidez, e não interpolação.
  const viewport = pagina.getViewport({
    scale: escalaDoPdf(original.width, original.height, areaMaximaDoAparelho()),
  });

  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const contexto = canvas.getContext('2d');
  if (contexto === null) throw new Error('sem contexto 2d');
  // Fundo branco: a página do PDF não pinta o papel, e o PNG guardaria o transparente.
  contexto.fillStyle = '#fff';
  contexto.fillRect(0, 0, canvas.width, canvas.height);

  await pagina.render({ canvas, canvasContext: contexto, viewport }).promise;

  return {
    // PNG: o desenho é linha e texto, e o JPEG borra exatamente isso.
    image: await dataUrlDe(await blobDoCanvas(canvas, 'image/png')),
    width: canvas.width,
    height: canvas.height,
  };
}

async function carregarImagem(arquivo: Blob): Promise<PlanDraft> {
  const url = URL.createObjectURL(arquivo);
  try {
    const imagem = await new Promise<HTMLImageElement>((resolve, reject) => {
      const elemento = new Image();
      elemento.onload = () => resolve(elemento);
      elemento.onerror = () => reject(new Error('imagem inválida'));
      elemento.src = url;
    });

    const tamanho = tamanhoDaImagem(
      imagem.naturalWidth,
      imagem.naturalHeight,
      areaMaximaDoAparelho(),
    );
    // No tamanho dela, a imagem é o próprio arquivo: os bytes que a pessoa escolheu.
    if (!tamanho.reduzida) {
      return {
        image: await dataUrlDe(arquivo),
        width: imagem.naturalWidth,
        height: imagem.naturalHeight,
      };
    }

    const canvas = document.createElement('canvas');
    canvas.width = tamanho.largura;
    canvas.height = tamanho.altura;
    const contexto = canvas.getContext('2d');
    if (contexto === null) throw new Error('sem contexto 2d');
    contexto.imageSmoothingQuality = 'high';
    contexto.drawImage(imagem, 0, 0, canvas.width, canvas.height);

    return {
      image: await dataUrlDe(await blobDoCanvas(canvas, 'image/png')),
      width: canvas.width,
      height: canvas.height,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * A página renderizada como arquivo para guardar no servidor, no lugar do original grande
 * demais (RN-248): JPEG, porque aqui o que conta é caber no limite — e, se ainda assim não
 * couber, a mesma página em larguras menores até caber. A tela continua com a página nítida;
 * isto é só o que se guarda.
 */
export async function paginaParaGuardar(
  pagina: PlanDraft,
  limiteDeBytes: number,
): Promise<{ readonly bytes: Uint8Array; readonly mimeType: string }> {
  const imagem = await new Promise<HTMLImageElement>((resolve, reject) => {
    const elemento = new Image();
    elemento.onload = () => resolve(elemento);
    elemento.onerror = () => reject(new Error('página inválida'));
    elemento.src = pagina.image;
  });
  for (const fator of [1, 0.75, 0.5, 0.35]) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(pagina.width * fator));
    canvas.height = Math.max(1, Math.floor(pagina.height * fator));
    const contexto = canvas.getContext('2d');
    if (contexto === null) throw new Error('sem contexto 2d');
    contexto.fillStyle = '#fff';
    contexto.fillRect(0, 0, canvas.width, canvas.height);
    contexto.imageSmoothingQuality = 'high';
    contexto.drawImage(imagem, 0, 0, canvas.width, canvas.height);
    const blob = await blobDoCanvas(canvas, 'image/jpeg', 0.92);
    if (blob.size <= limiteDeBytes) {
      return { bytes: new Uint8Array(await blob.arrayBuffer()), mimeType: 'image/jpeg' };
    }
  }
  throw new Error('a página renderizada não coube no limite');
}

function blobDoCanvas(canvas: HTMLCanvasElement, tipo: string, qualidade?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob === null ? reject(new Error('o canvas não gerou a imagem')) : resolve(blob)),
      tipo,
      qualidade,
    );
  });
}

function dataUrlDe(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () =>
      typeof leitor.result === 'string'
        ? resolve(leitor.result)
        : reject(new Error('não consegui ler a imagem'));
    leitor.onerror = () => reject(new Error('não consegui ler a imagem'));
    leitor.readAsDataURL(blob);
  });
}
